import React from "react";
import dayjs from "dayjs";
import { PiClock, PiMapPin } from "react-icons/pi";
import { useTranslation } from "react-i18next";

import { Bill } from "../../models";
import { durationFormatter } from "../../formatters";
import { useNavigate } from "react-router";
import { FaMapPin } from "react-icons/fa6";

interface BillPanelProps {
  bill: Bill;
  selectable: boolean;
  selected: boolean;
  onClick: () => void;
}

const BillPanel: React.FC<BillPanelProps> = ({ bill, selectable, selected, onClick }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [parkingDuration, setParkingDuration] = React.useState<number>(0);

  React.useEffect(() => {
    if (bill.outDatetime) {
      setParkingDuration(bill.outDatetime.diff(bill.inDatetime));
    } else {
      const updateDuration = () => setParkingDuration(dayjs().diff(bill.inDatetime));
      updateDuration();
      const interval = setInterval(updateDuration, 1000);
      return () => clearInterval(interval);
    }
  }, [bill.inDatetime, bill.outDatetime]);

  return (
    <>
      <div
        onClick={onClick}
        className="md:hidden lg:hidden py-6 px-8 space-y-6 rounded-2xl border bg-card text-card-foreground shadow"
        style={{
          borderColor: selected ? "var(--primary) !important" : "var(--card) !important",
        }}
      >
        <div className="flex-between gap-2">
          <div className="flex-center gap-2">
            {selectable && <input type="radio" className="accent-primary" checked={selected} />}
            <div className="flex-center gap-1 text-lg text-primary font-semibold border rounded-full px-4">
              {durationFormatter(parkingDuration)}
            </div>
          </div>
          <div className="flex-center gap-1">
            <FaMapPin />
            {bill.parkingName}
          </div>
        </div>
        <div className="flex-around">
          <div className="flex-center flex-col font-light col-span-2">
            <div>{t("bill.inTime")}</div>
            <div className="text-4xl font-semibold">{bill.inDatetime.format("HH:mm")}</div>
            <div>{bill.inDatetime.format("YYYY.MM.DD")}</div>
          </div>
          <div className="flex-center col-span-1">
            <PiClock size={"3rem"} />
          </div>
          <div className="flex-center flex-col font-light col-span-3">
            <div>{t("bill.amountToPay")}</div>
            <div className="text-4xl font-bold text-primary">{bill.amt.toLocaleString("mn")}₮</div>
          </div>
        </div>
        <button
          type="button"
          className={`mt-1 w-full text-lg font-bold hover:bg-primary/80 text-primary-foreground py-1.5 px-3 rounded-xl ${bill.active ? "bg-primary" : "bg-secondary"}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/payment/${bill.invoiceId}`);
          }}
        >
          {bill.active ? t("bill.leaveParking") : t("bill.payBill")}
        </button>
      </div>
      <div
        onClick={onClick}
        className="max-md:hidden lg:hidden py-4 px-8 space-y-4 rounded-4xl border bg-card text-card-foreground shadow"
        style={{
          borderColor: selected ? "var(--primary) !important" : "var(--card) !important",
        }}
      >
        <div className="flex-start gap-2">
          {selectable && <input type="radio" className="accent-primary" checked={selected} />}
          {!bill.active && (
            <>
              <div className="flex-center gap-1 text-lg text-primary font-semibold border rounded-full px-5">
                {durationFormatter(parkingDuration)}
              </div>
              <div className="flex-1"></div>
            </>
          )}
          <FaMapPin />
          <div className="font-semibold">{bill.parkingName}</div>
        </div>
        <div className="grid grid-rows-1 grid-cols-10">
          <div className="flex-between font-light col-span-6">
            <div className="flex-center flex-col font-light">
              <div>{t("bill.inTime")}</div>
              <div className="text-5xl font-normal">{bill.inDatetime.format("HH:mm")}</div>
              <div>{bill.inDatetime.format("YYYY.MM.DD")}</div>
            </div>
            <div className="bg-primary/25 text-primary rounded-full p-1">
              <PiClock size={"2.5rem"} />
            </div>
            {bill.active ? (
              <div className="flex-center flex-col font-light">
                <div>{t("payment.parkingDuration")}</div>
                <div className="text-5xl font-normal text-primary">
                  <div style={{ width: (durationFormatter(parkingDuration).length - 1.5).toString() + "ch" }}>
                    {durationFormatter(parkingDuration)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-center flex-col font-light">
                <div>{t("bill.outTime")}</div>
                <div className="text-5xl font-normal">{bill.outDatetime.format("HH:mm")}</div>
                <div>{bill.outDatetime.format("YYYY.MM.DD")}</div>
              </div>
            )}
          </div>
          <div className="flex-center py-4 col-span-1">
            <div className="border-r h-full"></div>
          </div>
          <div className="flex-center flex-col font-light col-span-3">
            <div>{t("bill.amountToPay")}</div>
            <div className="text-5xl leading-14 font-semibold text-primary">{bill.amt.toLocaleString("mn")}₮</div>
            <button
              type="button"
              className={`mt-1 w-full text-lg font-bold hover:bg-primary/80 text-primary-foreground py-1.5 px-3 rounded-xl ${bill.active ? "bg-primary" : "bg-secondary"}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/payment/${bill.invoiceId}`);
              }}
            >
              {bill.active ? t("bill.leaveParking") : t("bill.payBill")}
            </button>
          </div>
        </div>
      </div>
      <div
        onClick={onClick}
        className="max-lg:hidden py-6 px-8 space-y-4 rounded-4xl border bg-card text-card-foreground shadow relative"
        style={{
          borderColor: selected ? "var(--primary) !important" : "var(--card) !important",
        }}
      >
        <div className="grid" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
          <div className="flex-start gap-2 col-span-4" title={bill.parkingName}>
            {selectable && (
              <div
                className="shrink-0 p-0.5 rounded-full overflow-hidden border"
                style={{
                  borderColor: selected ? "var(--primary) !important" : "var(--muted-foreground) !important",
                }}
              >
                <div className={`w-4 h-4 rounded-full ${selected ? "bg-primary" : "bg-transparent"}`}></div>
              </div>
            )}
            <div className="flex-1 truncate text-2xl">{bill.parkingName}</div>
          </div>
          <div className="col-span-1 py-4 flex-center">
            <div className="h-full border-r"></div>
          </div>
          {bill.active ? (
            <div className="flex-center flex-col font-light col-span-4">
              <div>{t("bill.inTime")}</div>
              <div className="text-5xl font-normal">{bill.inDatetime.format("HH:mm")}</div>
              <div>{bill.inDatetime.format("YYYY.MM.DD")}</div>
            </div>
          ) : (
            <div className="flex-center flex-col font-light col-span-4">
              <div>{t("payment.parkingDuration")}</div>
              <div className="text-4xl font-normal flex-center gap-2">
                <div style={{ width: (durationFormatter(parkingDuration).length - 1.5).toString() + "ch" }}>
                  {durationFormatter(parkingDuration)}
                </div>
              </div>
            </div>
          )}
          <div className="col-span-1 py-4 flex-center">
            <div className="h-full border-r"></div>
          </div>
          {bill.active ? (
            <div className="flex-center flex-col font-light col-span-7">
              <div>{t("payment.parkingDuration")}</div>
              <div className="text-5xl font-normal text-primary flex-center gap-2">
                <PiClock size={"2.9rem"} />
                <div style={{ width: (durationFormatter(parkingDuration).length - 1.5).toString() + "ch" }}>
                  {durationFormatter(parkingDuration)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-between font-light col-span-7">
              <div className="flex-center flex-col">
                <div>{t("bill.inTime")}</div>
                <div className="text-4xl font-normal">{bill.inDatetime.format("HH:mm")}</div>
                <div>{bill.inDatetime.format("YYYY.MM.DD")}</div>
              </div>
              <div className="bg-primary/25 text-primary rounded-full p-1">
                <PiClock size={"2rem"} />
              </div>
              <div className="flex-center flex-col">
                <div>{t("bill.outTime")}</div>
                <div className="text-4xl font-normal">{bill.outDatetime.format("HH:mm")}</div>
                <div>{bill.outDatetime.format("YYYY.MM.DD")}</div>
              </div>
            </div>
          )}
          <div className="col-span-1 py-4 flex-center">
            <div className="h-full border-r"></div>
          </div>
          <div className="flex-center flex-col font-light col-span-6">
            <div>{t("bill.amountToPay")}</div>
            <div className="text-5xl leading-14 font-semibold text-primary">{bill.amt.toLocaleString("mn")}₮</div>
            <button
              type="button"
              className={`mt-1 w-full text-lg font-bold hover:bg-primary/80 text-primary-foreground py-1.5 px-3 rounded-xl ${bill.active ? "bg-primary" : "bg-secondary"}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/payment/${bill.invoiceId}`);
              }}
            >
              {bill.active ? t("bill.leaveParking") : t("bill.payBill")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BillPanel;
